/**
 * Shared presentational bits for the Skills Hub web surface (M2.4).
 * Follows the tabbio web design system: neutral surfaces, CSS-var colors,
 * Hugeicons glyphs, logical RTL props. No brand violet.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { SkillSummary, SkillTier } from '@tabbio/types';
import { HugeSymbol as MaterialSymbol } from '../icons/HugeSymbol';
import type { SkillInputField } from './skill-taxonomy';
import { SkillMarkdown } from './skill-markdown';
import { hasCvCustomContent } from './skill-diff';

// ── Tier badge (neutral, never colored) ────────────────────────────────────────

export function SkillTierBadge({ tier }: { tier: SkillTier | string }) {
  const { t } = useTranslation('common');
  const known = tier === 'free' || tier === 'plus' || tier === 'pro';
  const label = known
    ? t(`skills.tiers.${tier}`)
    : String(tier || '').trim() || t('skills.tiers.free');
  return <span className="tabbio-menu-badge">{label}</span>;
}

// ── Skill diff renderer ─────────────────────────────────────────────────────────
// Renders the structured CvDiff payload returned by a (dry) skill run:
//   { title?, headline?, summary?, skillGroups?, sectionEdits?, changeSummary?, nextActions? }
// Tolerant of unknown shapes: anything it doesn't recognise is ignored.

interface SectionEdit {
  section: string;
  field?: string;
  before?: string;
  after: string;
  rationale?: string;
}

interface SkillGroupChange {
  category: string;
  items: string[];
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    : [];
}

function asSectionEdits(value: unknown): SectionEdit[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const after = asString(e.after);
    const section = asString(e.section);
    if (!after || !section) return [];
    return [
      {
        section,
        field: asString(e.field) ?? undefined,
        before: asString(e.before) ?? undefined,
        after,
        rationale: asString(e.rationale) ?? undefined,
      },
    ];
  });
}

function asSkillGroups(value: unknown): SkillGroupChange[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const category = asString(e.category);
    const items = asStringArray(e.items);
    if (!category) return [];
    return [{ category, items }];
  });
}

export function skillDiffIsEmpty(diff: Record<string, unknown> | undefined): boolean {
  if (!diff) return true;
  const changeSummary = asStringArray(diff.changeSummary);
  const sectionEdits = asSectionEdits(diff.sectionEdits);
  const skillGroups = asSkillGroups(diff.skillGroups);
  const nextActions = asStringArray(diff.nextActions);
  const scalars = [diff.title, diff.headline, diff.summary].some((v) => asString(v));
  const outputText = asString(diff.outputText);
  return (
    changeSummary.length === 0 &&
    sectionEdits.length === 0 &&
    skillGroups.length === 0 &&
    nextActions.length === 0 &&
    !scalars &&
    !outputText &&
    // A cv_custom run writes structured section arrays and may carry none of
    // the narrative fields above (see `hasCvCustomContent`).
    !hasCvCustomContent(diff)
  );
}

const labelStyle = {
  fontSize: 12,
  fontWeight: 500 as const,
  color: 'var(--text-secondary)',
  marginBottom: 4,
};

function ScalarChange({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <p
        dir="auto"
        style={{
          margin: 0,
          fontSize: 14,
          lineHeight: '21px',
          color: 'var(--text-primary)',
          whiteSpace: 'pre-wrap',
        }}
      >
        {value}
      </p>
    </div>
  );
}

export function SkillDiffView({ diff }: { diff: Record<string, unknown> }) {
  const { t } = useTranslation('common');
  const changeSummary = asStringArray(diff.changeSummary);
  const sectionEdits = asSectionEdits(diff.sectionEdits);
  const skillGroups = asSkillGroups(diff.skillGroups);
  const nextActions = asStringArray(diff.nextActions);
  const title = asString(diff.title);
  const headline = asString(diff.headline);
  const summary = asString(diff.summary);
  const outputText = asString(diff.outputText);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {outputText ? <SkillMarkdown source={outputText} /> : null}

      {changeSummary.length > 0 ? (
        <div>
          <div style={labelStyle}>{t('skills.runDialog.changes')}</div>
          <ul style={{ margin: 0, paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {changeSummary.map((line, i) => (
              <li
                key={i}
                dir="auto"
                style={{ fontSize: 14, lineHeight: '21px', color: 'var(--text-primary)' }}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {title ? <ScalarChange label={t('skills.runDialog.titleField')} value={title} /> : null}
      {headline ? (
        <ScalarChange label={t('skills.runDialog.headlineField')} value={headline} />
      ) : null}
      {summary ? (
        <ScalarChange label={t('skills.runDialog.summaryField')} value={summary} />
      ) : null}

      {sectionEdits.map((edit, i) => (
        <div
          key={`${edit.section}-${i}`}
          style={{
            borderRadius: 14,
            background: 'rgba(0,0,0,0.04)',
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={labelStyle}>
            {edit.field ? `${edit.section} · ${edit.field}` : edit.section}
          </div>
          {edit.before ? (
            <p
              dir="auto"
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: '19px',
                color: 'var(--text-tertiary)',
                textDecoration: 'line-through',
                whiteSpace: 'pre-wrap',
              }}
            >
              {edit.before}
            </p>
          ) : null}
          <p
            dir="auto"
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: '21px',
              color: 'var(--text-primary)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {edit.after}
          </p>
          {edit.rationale ? (
            <p
              dir="auto"
              style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)' }}
            >
              {edit.rationale}
            </p>
          ) : null}
        </div>
      ))}

      {skillGroups.length > 0 ? (
        <div>
          <div style={labelStyle}>{t('skills.runDialog.skillsField')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {skillGroups.map((group, i) => (
              <div key={`${group.category}-${i}`}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {group.category}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {group.items.map((item, j) => (
                    <span
                      key={`${item}-${j}`}
                      dir="auto"
                      style={{
                        display: 'inline-flex',
                        padding: '4px 10px',
                        borderRadius: 9999,
                        background: 'rgba(0,0,0,0.04)',
                        fontSize: 13,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {nextActions.length > 0 ? (
        <div>
          <div style={labelStyle}>{t('skills.runDialog.nextActions')}</div>
          <ul style={{ margin: 0, paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {nextActions.map((line, i) => (
              <li
                key={i}
                dir="auto"
                style={{ fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ── Declarative run-screen inputs (tabbio/v2 `inputs[]`) ───────────────────────
// Renders a skill detail's declarative SkillInputField list: used by both the
// run dialog and the detail page's Run & tune panel when `inputsSchema` is the
// v2 array form. Values are keyed by field id in the run `inputs` payload.

const declaredLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  marginBottom: 8,
};

const declaredInputStyle: CSSProperties = {
  width: '100%',
  height: 52,
  borderRadius: 16,
  border: '1px solid var(--input-border)',
  background: 'var(--bg)',
  color: 'var(--text-primary)',
  fontSize: 15,
  fontFamily: 'inherit',
  paddingInline: 16,
  boxSizing: 'border-box',
};

const declaredTextareaStyle: CSSProperties = {
  width: '100%',
  minHeight: 110,
  borderRadius: 16,
  border: '1px solid var(--input-border)',
  background: 'var(--bg)',
  color: 'var(--text-primary)',
  fontSize: 15,
  lineHeight: '22px',
  fontFamily: 'inherit',
  padding: '14px 16px',
  boxSizing: 'border-box',
  resize: 'vertical',
};

const declaredSelectStyle: CSSProperties = {
  ...declaredInputStyle,
  paddingInline: '16px 40px',
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
};

// Re-exported from the shared taxonomy module so the create-CV flow - whose
// controller hook is platform-agnostic - can reach them without importing
// either platform twin.
export {
  declaredInputsComplete,
  declaredInputsPayload,
} from './skill-taxonomy';

export function SkillDeclaredInputs({
  fields,
  values,
  onChange,
  isRTL,
}: {
  fields: SkillInputField[];
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  isRTL: boolean;
}) {
  const { t } = useTranslation('common');
  return (
    <>
      {fields.map((field) => (
        <section key={field.id}>
          <div style={declaredLabelStyle} dir="auto">
            {field.label}
            {field.required ? (
              <span aria-hidden style={{ color: 'var(--text-tertiary)' }}>
                {' '}
                *
              </span>
            ) : null}
          </div>
          {field.type === 'textarea' ? (
            <textarea
              value={values[field.id] ?? ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              dir="auto"
              rows={4}
              style={declaredTextareaStyle}
            />
          ) : field.type === 'select' && field.options?.length ? (
            <div style={{ position: 'relative' }}>
              <select
                value={values[field.id] ?? ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                dir={isRTL ? 'rtl' : 'ltr'}
                style={{
                  ...declaredSelectStyle,
                  color: (values[field.id] ?? '')
                    ? 'var(--text-primary)'
                    : 'var(--text-tertiary)',
                }}
              >
                <option value="" disabled>
                  {field.placeholder || t('skills.runDialog.selectPlaceholder')}
                </option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <MaterialSymbol
                name="expand_more"
                size={20}
                style={{
                  position: 'absolute',
                  top: 15,
                  insetInlineEnd: 14,
                  color: 'var(--text-tertiary)',
                  pointerEvents: 'none',
                }}
              />
            </div>
          ) : (
            <input
              value={values[field.id] ?? ''}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              dir="auto"
              style={declaredInputStyle}
            />
          )}
        </section>
      ))}
    </>
  );
}

// ── Author display helper ──────────────────────────────────────────────────────

export function useSkillAuthorLabel() {
  const { t } = useTranslation('common');
  return (skill: Pick<SkillSummary, 'authorName' | 'shelf'>) => {
    if (skill.shelf === 'default') return t('skills.byTabbio');
    if (skill.authorName) return t('skills.by', { author: skill.authorName });
    return t('skills.byTabbio');
  };
}
